/**
 * Minimal WHIP publisher (RFC 9725): one POST with the SDP offer, DELETE to stop.
 * Non-trickle: we wait for ICE gathering so a single round trip is enough.
 */
export class WhipPublisher {
  pc: RTCPeerConnection;
  private resourceUrl: string | null = null;
  readonly endpoint: string;
  private token: string;

  constructor(endpoint: string, token: string) {
    this.endpoint = endpoint;
    this.token = token;
    this.pc = new RTCPeerConnection({ bundlePolicy: 'max-bundle' });
  }

  async publish(stream: MediaStream) {
    const pc = this.pc;
    for (const track of stream.getTracks()) {
      const t = pc.addTransceiver(track, { direction: 'sendonly', streams: [stream] });
      if (track.kind === 'video') {
        preferH264(t);
        // Cap bitrate: one upload, many viewers via CDN; 2.5 Mbps is plenty for 720p.
        const params = t.sender.getParameters();
        params.encodings = [{ maxBitrate: 2_500_000, maxFramerate: 30 }];
        await t.sender.setParameters(params).catch(() => undefined);
      }
    }
    await pc.setLocalDescription(await pc.createOffer());
    await waitForIce(pc, 2000);
    const res = await fetch(this.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp', Authorization: `Bearer ${this.token}` },
      body: pc.localDescription!.sdp,
    });
    if (res.status === 401 || res.status === 403) throw new PublishError('unauthorized', 'This stream link is no longer accepting video.');
    if (!res.ok) throw new PublishError('server', `The streaming server answered ${res.status}.`);
    const location = res.headers.get('Location');
    if (location) this.resourceUrl = new URL(location, this.endpoint).toString();
    await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
  }

  /** Swap the video or audio source without renegotiating (camera flip, screen share). */
  async replaceTrack(kind: 'audio' | 'video', track: MediaStreamTrack | null) {
    const sender = this.pc.getSenders().find((s) => s.track?.kind === kind || (s.track === null && kind === 'video'));
    await sender?.replaceTrack(track);
  }

  async stop() {
    const url = this.resourceUrl;
    this.resourceUrl = null;
    this.pc.close();
    // keepalive: also sent when the tab is closing (see the Go Live page's pagehide).
    if (url) await fetch(url, { method: 'DELETE', keepalive: true, headers: { Authorization: `Bearer ${this.token}` } }).catch(() => undefined);
  }
}

export class PublishError extends Error {
  constructor(
    readonly code: 'unauthorized' | 'server' | 'unsupported',
    message: string,
  ) {
    super(message);
  }
}

/** H.264 can be stream-copied into HLS without transcoding, so ask the browser for it first. */
function preferH264(t: RTCRtpTransceiver) {
  const caps = RTCRtpSender.getCapabilities?.('video');
  if (!caps || typeof t.setCodecPreferences !== 'function') return;
  const score = (c: RTCRtpCodec) => {
    if (c.mimeType !== 'video/H264') return 0;
    const fmtp = c.sdpFmtpLine ?? '';
    return 2 + (fmtp.includes('packetization-mode=1') ? 2 : 0) + (fmtp.includes('profile-level-id=42e01f') ? 1 : 0);
  };
  const sorted = [...caps.codecs].sort((a, b) => score(b) - score(a));
  try {
    t.setCodecPreferences(sorted);
  } catch {
    /* browser refused; MediaMTX + the packager handle other codecs */
  }
}

function waitForIce(pc: RTCPeerConnection, timeoutMs: number) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const done = () => {
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => pc.iceGatheringState === 'complete' && done();
    pc.addEventListener('icegatheringstatechange', check);
    setTimeout(done, timeoutMs);
  });
}

export function webrtcSupported() {
  return typeof RTCPeerConnection !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}
